import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { includes } from 'lodash';
import { isAnyTrayItemHovered, getTraySelectedSegmentId } from '@selectors/map-selector';

const getShouldHighlightSegment = (shouldHighlight, selectedSegmentId = null) => {
  if (!shouldHighlight) {
    return () => false;
  }
  if (selectedSegmentId) {
    return segmentId => selectedSegmentId === segmentId;
  }
  return () => true;
};

/**
 * Custom hook to determine if an item should be highlighted.
 * @param {number} itemId - The ID of the item.
 * @param {Function} trayItemIdsSelector - Selector for tray item IDs.
 * @param {Function} hoveredItemIdSelector - Selector for hovered item ID.
 * @returns {object} Object with shouldHighlight, shouldHighlightSegment, and selectedSegmentId.
 */
export const useShouldHighlight = (itemId, trayItemIdsSelector, hoveredItemIdSelector = () => null) => {
  const hovering = useSelector(isAnyTrayItemHovered);
  const hoveredId = useSelector(hoveredItemIdSelector);
  const selectedSegmentId = useSelector(getTraySelectedSegmentId);
  const trayItemIds = useSelector(trayItemIdsSelector);

  const { shouldHighlight, shouldHighlightSegment } = useMemo(() => {
    let highlight = false;
    let highlightSegment;

    if (hovering) {
      highlight = hoveredId === itemId;
      // When hovering we should disregard the selectedSegmentId, because the highlight is for another entity.
      highlightSegment = getShouldHighlightSegment(highlight);
    } else {
      highlight = includes(trayItemIds, itemId);
      highlightSegment = getShouldHighlightSegment(highlight, selectedSegmentId);
    }

    return { shouldHighlight: highlight, shouldHighlightSegment: highlightSegment };
  }, [hoveredId, hovering, itemId, selectedSegmentId, trayItemIds]);

  return { shouldHighlight, shouldHighlightSegment, selectedSegmentId };
};
